import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import Image from 'utils/image';
import Media from 'utils/media';
import PlayIcon from 'utils/icon/icon-play.svg';
import { useProduct } from './product-context';
import ProductBadges from '../product-card/product-badges';

const ProductGallery = () => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const { selectedOptions, product, currentVariant } = useProduct();
  const swiperRef = useRef(null);

  const gallery = useMemo(() => {
    const colorOption = selectedOptions?.find((o) => o.name.toLowerCase() === 'color');
    // find a variant that matches the colorOption
    // if there is no currentVariant as size hasn't been selected
    const selectedVariant =
      currentVariant ??
      product.variants.find((v) => {
        return v.selectedOptions.find((o) => o.name.toLowerCase() === 'color' && o.value === colorOption.value);
      });

    const variantImages = product.media.filter((m) => m.previewImage.id === selectedVariant?.featured_media.id);
    if (!colorOption) return variantImages;
    const matchingVariants = product.variants.filter((v) => {
      return v.selectedOptions.find((o) => o.name.toLowerCase() === 'color' && o.value === colorOption.value);
    });

    if (matchingVariants.length === 0) return variantImages;
    const variantMedia = [];

    variantImages.forEach((m) => {
      variantMedia.push(m);
    });

    matchingVariants.forEach((v) => {
      v.gallery.forEach((m) => {
        variantMedia.push(m);
      });
    });

    const uniqueMediaIds = [...new Set(variantMedia.map((m) => m.previewImage.id))];
    const images = uniqueMediaIds.map((id) => {
      return variantMedia.find((m) => m.previewImage.id === id);
    });

    return images ?? variantImages;
  }, [selectedOptions, product, currentVariant]);

  useEffect(() => {
    swiperRef.current?.swiper?.update();
  }, [gallery]);

  useEffect(() => {
    // remove aria attributes from Swiper component
    if (swiperRef.current) {
      const [swiperWrapper, swiperNotification] = swiperRef.current.children;

      swiperWrapper.removeAttribute('aria-live');
      swiperNotification.removeAttribute('aria-live');
      swiperNotification.removeAttribute('aria-atomic');
    }
  }, []);

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      swiperRef.current?.swiper?.slideTo(index);
    }
  };

  return (
    <div className="-mx-3 w-[calc(100%+theme(spacing.6))] flex-col gap-5 md:mx-0 md:flex md:w-full lg:flex-row">
      <div className="hidden w-full flex-none md:block lg:w-14.5">
        <Swiper
          onSwiper={setThumbsSwiper}
          spaceBetween={8}
          slidesPerView="auto"
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          breakpoints={{
            1024: {
              direction: 'vertical',
            },
          }}
        >
          {gallery.map((img, index) => (
            <SwiperSlide
              tag="button"
              key={img.previewImage.id}
              tabIndex={0}
              className="!size-14.5 border-b border-transparent [&.swiper-slide-thumb-active]:border-black"
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <Image
                {...img.previewImage}
                classes="h-full w-full object-cover rounded"
                srcWidths={{ sm: 200 }}
              />
              {img.mediaContentType.toLowerCase().includes('video') && (
                <div className="pointer-events-none absolute bottom-0.5 left-0.5 inline-flex h-7.5 w-7.5 items-center justify-center rounded-full border border-black bg-white transition-colors duration-200 ease-in-out hover:border-brand-tangerine">
                  <span className="icon-play">
                    <PlayIcon className="relative left-px h-auto w-3" />
                  </span>
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="w-full lg:w-[calc(100%-theme(spacing.20))]">
        <Swiper
          ref={swiperRef}
          spaceBetween={8}
          slidesPerView={1.2}
          loop={true}
          breakpoints={{
            768: {
              slidesPerView: 1,
              loop: false,
            },
          }}
          modules={[FreeMode, Navigation, Thumbs]}
          thumbs={{ swiper: thumbsSwiper }}
        >
          {gallery.map((media, index) => {
            const caption = media?.previewImage.alt?.split('::')[1];
            return (
              <SwiperSlide
                key={media.previewImage.id}
                className="overflow-hidden rounded"
                tabIndex={0}
              >
                {index === 0 && (
                  <ProductBadges
                    product={product}
                    currentVariant={currentVariant}
                  />
                )}
                <Media
                  {...media}
                  classes="w-full h-auto"
                  srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
                />
                {!!caption && <div className="caption bg-brand-light-beige px-2 py-0.5 text-center">{caption}</div>}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default ProductGallery;
